import React, { useState } from 'react';
import { useToast } from '../../sectepe-ui/index';
import leadSyncService, { EnrichAndSyncResult, LemlistCampaign } from '../../services/leadSyncService';
import { Icon } from '../../components/icons';
import { fehlerText } from '../format';
import { ErgebnisTabelle } from './ErgebnisTabelle';
import { KampagnenAuswahl } from './KampagnenPanel';

interface Props {
  ausgewaehlt: string[];
  kampagnen: LemlistCampaign[];
  laedtKampagnen: boolean;
  onKampagnenLaden: () => void;
  onGeladen: () => Promise<void> | void;
  onFehler: (text: string | null) => void;
}

/** Anreichern und senden in einem Lauf: Auswahl → Clay → Lemlist. */
export const DurchlaufPanel: React.FC<Props> = ({ ausgewaehlt, kampagnen, laedtKampagnen, onKampagnenLaden, onGeladen, onFehler }) => {
  const { push } = useToast();
  const [kampagne, setKampagne] = useState('');
  const [tabelle, setTabelle] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<EnrichAndSyncResult | null>(null);

  const starten = async () => {
    setLaeuft(true);
    onFehler(null);
    try {
      const r = await leadSyncService.enrichAndSync(ausgewaehlt, kampagne, tabelle.trim() || undefined);
      setErgebnis(r);
      push({ message: `${r.enrichResult.enriched} angereichert, ${r.syncResult.synced} in die Kampagne aufgenommen.`, severity: 'success', duration: 8000 });
      await onGeladen();
    } catch (e) {
      onFehler(fehlerText(e, 'Durchlauf fehlgeschlagen'));
    } finally {
      setLaeuft(false);
    }
  };

  const schritte = [
    { nr: 1, label: 'Leads auswählen', hinweis: ausgewaehlt.length ? `${ausgewaehlt.length} ausgewählt` : 'oben in der Liste', state: ausgewaehlt.length ? 'done' : 'ready' },
    { nr: 2, label: 'Mit Clay anreichern', hinweis: tabelle.trim() ? 'über Clay-Tabelle' : 'direkt über die API', state: 'ready' },
    { nr: 3, label: 'In Kampagne aufnehmen', hinweis: kampagne ? kampagnen.find((k) => k._id === kampagne)?.name ?? kampagne : 'Kampagne wählen', state: kampagne ? 'done' : 'blocked' },
  ];

  return (
    <div className="lead-panel">
      <section className="card" aria-label="Anreichern und senden">
        <h3 className="card-title">Anreichern &amp; senden</h3>
        <p className="card-description">Ein Lauf für neue Leads: erst Clay, dann Lemlist. Was Clay nicht ergänzen kann, geht trotzdem in die Kampagne.</p>
        <ol className="durchlauf-schritte" aria-label="Schritte">
          {schritte.map((s) => (
            <li key={s.nr} className="durchlauf-schritt" data-state={s.state}>
              <span className="durchlauf-schritt__nr">{s.nr}</span>
              <span className="durchlauf-schritt__text"><strong>{s.label}</strong><small>{s.hinweis}</small></span>
            </li>
          ))}
        </ol>
        <div className="akte-felder">
          <KampagnenAuswahl id="kampagne-durchlauf" kampagnen={kampagnen} wert={kampagne} onChange={setKampagne} laedt={laedtKampagnen} onLaden={onKampagnenLaden} />
          <label className="lead-panel__feld">
            Clay-Tabelle (optional)
            <input type="text" placeholder="leer lassen für direkte Anreicherung" value={tabelle} onChange={(e) => setTabelle(e.target.value)} />
          </label>
        </div>
        <button type="button" className="btn btn-primary" onClick={starten} disabled={laeuft || ausgewaehlt.length === 0 || !kampagne}>
          <Icon name="zap" size={14} /> {laeuft ? 'Läuft…' : `Durchlauf starten (${ausgewaehlt.length} Leads)`}
        </button>
      </section>

      {ergebnis && (
        <div className="grid-2-col">
          <ErgebnisTabelle titel="Clay-Anreicherung" gut={ergebnis.enrichResult.enriched} gutLabel="angereichert" schlecht={ergebnis.enrichResult.failed} details={ergebnis.enrichResult.details} />
          <ErgebnisTabelle titel="Lemlist-Kampagne" gut={ergebnis.syncResult.synced} gutLabel="aufgenommen" schlecht={ergebnis.syncResult.failed} details={ergebnis.syncResult.details} />
        </div>
      )}
    </div>
  );
};
